/* eslint react/prop-types: 0, react/display-name: 0 */

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

// Components
import UnavailableItem from './buttons/CartUnavailableItem';
import CartCustomHold from './buttons/CartCustomHold';

import CartPurchaseButton from './buttons/CartPurchaseButton';
import CartMakeOfferButton from './buttons/CartMakeOfferButton';
import CartRequestPriceButton from './buttons/CartRequestPriceButton';
import CartCheckoutOfferButton from './buttons/CartCheckoutOfferButton';

function getTrackingLabel(item) {
    const labelInfo = [];

    const isNegotiable = get(item, 'pricing.negotiable');
    labelInfo.push(`neg=${isNegotiable ? 'y' : 'n'}`);

    const shipmentPrice = get(item, 'shipmentQuotes[0].totalAmount.amount');
    labelInfo.push(`ship=${shipmentPrice ? 'y' : 'n'}`);

    const acceptReturnPolicy = get(item, 'returnPolicy.accept');
    labelInfo.push(`ret=${acceptReturnPolicy ? 'y' : 'n'}`);

    return labelInfo.join('|');
}

const CartItemCTA = props => {
    return (
        <CurrencyProvider
            render={({ currency }) => {
                const { item, isUnavailable, isPurchasable, quantitySelected } = props;
                const itemId = get(item, 'serviceId');
                const transaction = get(item, 'lastTransaction');
                const ctas = get(item, 'displayInfo.ctas') || {};
                const priceDisplays = get(item, 'displayInfo.pricing') || {};

                let unavailableType = 'unavailable';
                let header = null;

                if (priceDisplays.showSold) {
                    unavailableType = 'sold';
                    header = <FormattedMessage id="CartItemCTA.sold" defaultMessage="Sold" />;
                } else if (priceDisplays.showOnHold) {
                    unavailableType = 'onHold';
                    header = <FormattedMessage id="CartItemCTA.onHold" defaultMessage="On Hold" />;
                }

                const returnButtons = [];
                const trackingLabel = getTrackingLabel(item);

                if (priceDisplays.showOnHoldCustom) {
                    returnButtons.push(
                        <CartCustomHold item={item} key={`onHoldCustom-${itemId}`} />
                    );
                }

                if (ctas.showRequestPrice) {
                    returnButtons.push(
                        <CartRequestPriceButton
                            currency={currency}
                            item={item}
                            key={`requestCart-${itemId}`}
                        />
                    );
                } else if (ctas.showAcceptOffer) {
                    returnButtons.push(
                        <CartCheckoutOfferButton
                            item={item}
                            transaction={transaction}
                            trackingLabel={trackingLabel}
                            key={`checkoutOffer-${itemId}`}
                        />
                    );
                } else {
                    if (ctas.showPurchase) {
                        returnButtons.push(
                            <CartPurchaseButton
                                item={item}
                                trackingLabel={trackingLabel}
                                quantitySelected={quantitySelected}
                                key={`purchaseCart-${itemId}`}
                            />
                        );
                    }
                    if (ctas.showMakeOffer) {
                        returnButtons.push(
                            <CartMakeOfferButton
                                item={item}
                                trackingLabel={trackingLabel}
                                currency={currency}
                                quantitySelected={quantitySelected}
                                key={`makeOffer-${itemId}`}
                            />
                        );
                    }
                }

                return isPurchasable && !priceDisplays.showOnHold ? (
                    <div>{returnButtons}</div>
                ) : (
                    <UnavailableItem
                        type={unavailableType}
                        header={header}
                        copy={
                            isUnavailable ? (
                                <FormattedMessage
                                    id="CartItemCTA.unavailable"
                                    defaultMessage="Sorry, this item is not currently available"
                                />
                            ) : null
                        }
                    />
                );
            }}
        />
    );
};

CartItemCTA.propTypes = {
    item: PropTypes.object.isRequired,
    isUnavailable: PropTypes.bool,
    isPurchasable: PropTypes.bool,
    quantitySelected: PropTypes.string,
};

export default createFragmentContainer(CartItemCTA, {
    item: graphql`
        fragment CartItemCTA_item on Item {
            ...CartCustomHold_item
            ...CartRequestPriceButton_item
            ...CartCheckoutOfferButton_item
            ...CartPurchaseButton_item
            ...CartMakeOfferButton_item
            serviceId
            returnPolicy {
                accept
            }
            shipmentQuotes {
                totalAmount {
                    amount
                }
            }
            pricing {
                negotiable
            }
            lastTransaction(buyerId: $userId) @include(if: $hasUserId) {
                ...CartCheckoutOfferButton_transaction
            }
            displayInfo(buyerId: $userId, page: pdp) {
                ctas {
                    showAcceptOffer
                    showMakeOffer
                    showPurchase
                    showRequestPrice
                }
                pricing {
                    showOnHold
                    showOnHoldCustom
                    showSold
                }
            }
        }
    `,
});
